<!--
  Panel de QA del offline. Herramienta de PRUEBA, no una feature del producto.

  Existe porque probar el offline a mano tiene un ritual que nadie recuerda: el
  service worker no llama a `skipWaiting()` ni a `clients.claim()` —a propósito,
  para no servir chunks mezclados de dos builds—, así que después de cada deploy
  hay que desregistrar el SW viejo y recargar DOS veces (la primera instala el
  nuevo, la segunda es la primera página que queda controlada por él). Mientras
  no lo hagas seguís probando el build anterior por más que recargues.

  "Reiniciar service worker" hace eso en un paso: desregistra, borra los caches y
  registra el SW de nuevo a mano. Como en ese momento no queda ninguno activo, el
  nuevo activa apenas termina de instalar y alcanza con la única recarga del
  final. NO toca IndexedDB ni localStorage: se conservan la sesión, el cache de
  datos y la outbox con lo que haya sin sincronizar.

  El panel además muestra la outbox en vivo, que es lo que hay que mirar en casi
  todos los casos de prueba, sin tener que navegar IndexedDB en DevTools.

  VISIBILIDAD: solo cuando la app le habla a un backend que no es producción
  (ver `$lib/qa/backendDePrueba`). En producción el componente no renderiza nada, ni
  siquiera dentro de la app nativa — donde el host es `localhost` y por eso
  mirarlo a él no alcanzaba. La misma lógica existe como script
  de consola en `scripts/qa-offline/` para quien prefiera no depender de un
  deploy; si tocás una, actualizá la otra.
-->
<script>
    import { onMount } from "svelte";
    import { liveQuery } from "dexie";
    import { outboxDb } from "$lib/outbox/outboxDb";
    import { viewport } from "$lib/stores/viewport.svelte";
    import { esBackendDePrueba } from "$lib/qa/backendDePrueba";

    // En celu tiene que despejar la NavegacionBottom (--cf-nav-h) + la
    // safe-area inferior, o queda debajo de la barra del sistema; en
    // escritorio (sin barra inferior) se apoya abajo. Igual que
    // BarraAccionesSeleccion.
    let esCelu = $derived(viewport.esCelu);

    let visible = $state(false);
    let abierto = $state(false);
    let cacheActivo = $state("");
    let controlada = $state(false);
    let enLinea = $state(true);
    let mensaje = $state("");
    let trabajando = $state(false);

    // Se resuelve en onMount y no al evaluar el módulo: las páginas se
    // prerenderizan, y mirar `location` durante el SSR rompe la hidratación.
    onMount(() => {
        visible = esBackendDePrueba(import.meta.env.VITE_RUTA_JAVA_SERVER);
        if (!visible) return;
        refrescarEstado();

        // El estado de red es EL dato que más confunde al probar: si el navegador
        // no está realmente offline, una escritura se encola y drena en el mismo
        // instante — se ve igual que si nunca hubiera pasado por la outbox, y el
        // tester reporta "no me lo marcó como pendiente". Mostrarlo a la vista
        // evita esa hora perdida.
        enLinea = navigator.onLine;
        const online = () => (enLinea = true);
        const offline = () => (enLinea = false);
        window.addEventListener("online", online);
        window.addEventListener("offline", offline);
        return () => {
            window.removeEventListener("online", online);
            window.removeEventListener("offline", offline);
        };
    });

    async function refrescarEstado() {
        if (!("serviceWorker" in navigator)) return;
        // `controller` en null = la página NO pasa por el SW. Es exactamente el
        // estado en el que la navegación offline falla, y el que este panel evita.
        controlada = navigator.serviceWorker.controller != null;
        try {
            const keys = await caches.keys();
            cacheActivo = keys[0] ?? "(sin cache)";
        } catch {
            cacheActivo = "(no disponible)";
        }
    }

    async function reiniciarSW() {
        if (!navigator.onLine) {
            mensaje = "Estás offline. Volvé a conectarte: hay que rebajar el precache.";
            return;
        }
        trabajando = true;
        mensaje = "Desregistrando…";
        try {
            const registros = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registros.map((r) => r.unregister()));

            mensaje = "Borrando caches…";
            const viejos = await caches.keys();
            await Promise.all(viejos.map((k) => caches.delete(k)));

            // Mismo registro que hace SvelteKit desde index.html (script clásico).
            mensaje = "Instalando el build nuevo…";
            await navigator.serviceWorker.register("/service-worker.js");
            // `ready` resuelve recién cuando el SW quedó activo, y para eso tuvo
            // que terminar el precache (está en el waitUntil del install).
            await navigator.serviceWorker.ready;

            mensaje = "Listo, recargando…";
            location.reload();
        } catch (err) {
            console.error("[qa-offline]", err);
            mensaje = `Falló: ${err?.message ?? err}`;
            trabajando = false;
        }
    }

    // Misma fuente que el badge de sincronización, pero con el detalle por fila.
    // `synced` no aparece nunca: al confirmarse, la fila se BORRA de la tabla.
    const filas = liveQuery(() => outboxDb.outbox.orderBy("createdAt").toArray());

    function verPayloads(rows) {
        for (const o of rows) {
            console.groupCollapsed(`payload · #${o.id} ${o.entity} ${o.operation}`);
            console.log(o.payload);
            console.groupEnd();
        }
        mensaje = `${rows.length} payload(s) impresos en la consola.`;
    }

    const colorEstado = (s) =>
        s === "conflict" || s === "error"
            ? "text-cf-danger"
            : s === "in_flight"
              ? "text-amber-600"
              : "text-cf-text-sub";
</script>

{#if visible}
    {#if abierto}
        <div
            class="fixed left-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-2xl bg-cf-surface p-3 text-sm shadow-lg ring-1 ring-cf-border"
            style={`bottom: ${esCelu ? "calc(var(--cf-nav-h) + 1rem + var(--cf-safe-bottom))" : "1rem"}`}
        >
            <div class="mb-2 flex items-center justify-between">
                <span class="font-semibold text-cf-text">QA offline</span>
                <button
                    type="button"
                    class="px-2 text-cf-text-sub hover:text-cf-text"
                    onclick={() => (abierto = false)}
                    aria-label="Cerrar panel de QA">✕</button
                >
            </div>

            <dl class="mb-3 space-y-1 text-xs text-cf-text-sub">
                <div class="flex justify-between gap-2">
                    <dt>Red</dt>
                    <dd class={enLinea ? "text-cf-text-sub" : "font-semibold text-amber-600"}>
                        {enLinea ? "online — NO estás probando offline" : "OFFLINE"}
                    </dd>
                </div>
                <div class="flex justify-between gap-2">
                    <dt>Cache activo</dt>
                    <dd class="truncate font-mono" title={cacheActivo}>{cacheActivo}</dd>
                </div>
                <div class="flex justify-between gap-2">
                    <dt>Página controlada por el SW</dt>
                    <dd class={controlada ? "text-cf-primary" : "text-cf-danger"}>
                        {controlada ? "sí" : "NO — recargá"}
                    </dd>
                </div>
            </dl>

            <button
                type="button"
                class="mb-3 w-full rounded-lg bg-cf-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={trabajando}
                onclick={reiniciarSW}
            >
                {trabajando ? "Trabajando…" : "Reiniciar service worker"}
            </button>

            <div class="mb-1 flex items-center justify-between">
                <span class="text-xs font-semibold uppercase tracking-wide text-cf-text-sub">
                    Outbox ({$filas?.length ?? 0})
                </span>
                {#if $filas && $filas.length > 0}
                    <button
                        type="button"
                        class="text-xs underline text-cf-text-sub hover:text-cf-text"
                        onclick={() => verPayloads($filas)}>ver payloads</button
                    >
                {/if}
            </div>

            {#if !$filas || $filas.length === 0}
                <p class="text-xs text-cf-text-sub">Vacía — no hay nada sin sincronizar.</p>
            {:else}
                <ul class="max-h-52 space-y-1 overflow-y-auto text-xs">
                    {#each $filas as f (f.id)}
                        <li class="flex justify-between gap-2 border-b border-cf-border pb-1">
                            <span class="truncate">
                                <span class="font-mono">#{f.id}</span>
                                {f.entity}
                                <span class="font-semibold">{f.operation}</span>
                                {#if f.resourceId != null}
                                    <span class="text-cf-text-sub">→ {f.resourceId}</span>
                                {/if}
                            </span>
                            <span class={colorEstado(f.status)}>{f.status}</span>
                        </li>
                    {/each}
                </ul>
            {/if}

            {#if mensaje}
                <p class="mt-2 text-xs text-cf-text-sub">{mensaje}</p>
            {/if}
        </div>
    {:else}
        <!-- Cerrado igual muestra el estado de red: es el dato que hace que una
             sesión de prueba entera sea válida o no. -->
        <button
            type="button"
            class="fixed left-4 z-50 rounded-full px-3 py-2 text-xs font-semibold shadow-lg ring-1 ring-cf-border {enLinea
                ? 'bg-cf-surface text-cf-text-sub'
                : 'bg-amber-500 text-white'}"
            style={`bottom: ${esCelu ? "calc(var(--cf-nav-h) + 1rem + var(--cf-safe-bottom))" : "1rem"}`}
            onclick={async () => {
                abierto = true;
                await refrescarEstado();
            }}
            title="Herramientas de prueba del offline"
        >
            QA{enLinea ? "" : " · OFFLINE"}</button
        >
    {/if}
{/if}
